import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePartesTrabajoContext } from '@/contexts/PartesTrabajoContext';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useAuth } from '@/contexts/AuthContext';
import { PartesTrabajoFilters, type PartesFilters } from '@/components/partes/PartesTrabajoFilters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { SignatureCanvas, SignatureCanvasRef } from '@/components/SignatureCanvas';
import { Plus, FileText, Clock, User, Download, MapPin, Euro, CreditCard, Shield, AlertTriangle, ClipboardCheck, Package, MoreHorizontal, Paperclip, ClipboardList, CheckCircle, LayoutGrid, List } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TimeSelect } from '@/components/ui/time-select';
import { OrdenTrabajoLink } from '@/components/ordenes/OrdenTrabajoLink';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { downloadParteTrabajoePdf } from '@/lib/parteTrabajoePdfUtils';
import { ParteTrabajoDocumentos } from '@/components/ParteTrabajoDocumentos';
import { supabase } from '@/integrations/supabase/client';

export default function PartesTrabajo() {
  const { partes, loading, createParte, updateParte, getPartesWithOrdenData, getFilteredPartes, getEstadoCounts } = usePartesTrabajoContext();
  const { getOrdenesDisponiblesParaParte } = useOrdenesTrabajoContext();
  const { clientes, cargarClientes } = useSecurityContext();
  const { perfil, user } = useAuth();
  
  const [showForm, setShowForm] = useState(false);
  const [nextNumero, setNextNumero] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [createdParteId, setCreatedParteId] = useState<string | null>(null);
  const [createdParteNumero, setCreatedParteNumero] = useState<string | null>(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    if (typeof window === 'undefined') return 'cards';
    return (localStorage.getItem('partes_view_mode') as 'cards' | 'list') || 'cards';
  });
  useEffect(() => {
    try { localStorage.setItem('partes_view_mode', viewMode); } catch {}
  }, [viewMode]);
  const [filters, setFilters] = useState<PartesFilters>({
    search: '',
    estado: '',
    tipoIntervencion: '',
    fechaDesde: '',
    fechaHasta: '',
    tecnicoId: '',
    clienteId: '',
    conOT: 'all',
  });
  const [formData, setFormData] = useState({
    orden_trabajo_id: '',
    cliente_libre: '',
    cliente_id: '',
    fecha_trabajo: new Date().toISOString().split('T')[0],
    hora_inicio: '',
    hora_fin: '',
    descripcion_trabajo: '',
    tipo_intervencion: 'otros' as const,
    nota_interna: '',
    estado: 'borrador' as const,
    tiene_desplazamiento: false,
    forma_pago: '',
    importe_sin_iva: '',
    iva_porcentaje: 21,
    resultado_intervencion: '' as 'resuelto' | 'no_resuelto' | 'no_realizado' | '',
    requiere_nueva_visita: false,
    motivo_no_resuelto: '',
    materiales_necesarios: '',
    direccion_intervencion: '',
    codigo_postal_intervencion: '',
    poblacion_intervencion: '',
  });

  // Get OTs available for creating partes
  const ordenesAsignadas = getOrdenesDisponiblesParaParte();

  const tecnicoSignatureRef = useRef<SignatureCanvasRef>(null);
  const clienteSignatureRef = useRef<SignatureCanvasRef>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Clientes ampliados solo para el formulario de parte (todos los de la empresa)
  const [clientesParaParte, setClientesParaParte] = useState<any[]>([]);

  // Ensure client data is loaded
  useEffect(() => {
    if (clientes.length === 0) {
      cargarClientes();
    }
  }, [clientes.length, cargarClientes]);

  // Cargar clientes de empresa para el desplegable del formulario (incluye los no asignados al técnico)
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc('get_clientes_para_formulario_trabajo');
      if (!error && data) setClientesParaParte(data);
    })();
  }, []);

  // Lookup unificado: prioriza el listado ampliado, cae al del contexto
  const findClienteForForm = (id?: string | null) =>
    (id && (clientesParaParte.find(c => c.id === id) || clientes.find(c => c.id === id))) || null;

  // Preview next numero_parte without consuming it
  useEffect(() => {
    if (showForm) {
      const fetchNextNumero = async () => {
        try {
          const currentYear = new Date().getFullYear().toString().slice(-2);
          const { data, error } = await supabase
            .from('partes_trabajo')
            .select('numero_parte')
            .like('numero_parte', `${currentYear}%`)
            .order('numero_parte', { ascending: false })
            .limit(1);
          
          if (!error && data && data.length > 0 && data[0].numero_parte) {
            const lastNum = parseInt(data[0].numero_parte.slice(2), 10);
            setNextNumero(`${currentYear}${String(lastNum + 1).padStart(3, '0')}`);
          } else if (!error) {
            setNextNumero(`${currentYear}001`);
          }
        } catch (e) {
          console.error('Error previewing next numero_parte:', e);
        }
      };
      fetchNextNumero();
    } else {
      setNextNumero(null);
    }
  }, [showForm]);

  // Handle URL parameters to pre-fill form from OT detail view
  useEffect(() => {
    const ordenIdParam = searchParams.get('orden_id');
    const clienteIdParam = searchParams.get('cliente_id');
    
    if (ordenIdParam) {
      // Open form automatically
      setShowForm(true);
      
      // Pre-fill form with OT data
      setFormData(prev => ({
        ...prev,
        orden_trabajo_id: ordenIdParam,
        cliente_id: clienteIdParam || ''
      }));
      
      // Clear URL params to prevent re-triggering on refresh
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!perfil?.user_id) {
      toast({
        title: "Error",
        description: "Usuario no autenticado",
        variant: "destructive",
      });
      return;
    }

    // Resolver cliente efectivo (puede venir de OT, cliente seleccionado o cliente libre)
    const tieneOT = !!(formData.orden_trabajo_id && formData.orden_trabajo_id !== 'none');
    const otSeleccionada = tieneOT ? ordenesAsignadas.find(ot => ot.id === formData.orden_trabajo_id) : null;
    const clienteIdEfectivo = otSeleccionada?.cliente_id || formData.cliente_id || null;
    const clienteEfectivo: any = findClienteForForm(clienteIdEfectivo);
    const clienteTieneDireccion = !!(clienteEfectivo?.direccion && String(clienteEfectivo.direccion).trim());

    // Firmas (capturadas antes de validar para poder comprobarlas)
    const firmaTecnico = tecnicoSignatureRef.current?.getSignature() || '';
    const firmaCliente = clienteSignatureRef.current?.getSignature() || '';

    // Validación de campos obligatorios con mensajes claros
    const camposFaltantes: string[] = [];
    if (!formData.fecha_trabajo) camposFaltantes.push('Fecha');
    if (!formData.hora_inicio) camposFaltantes.push('Hora de inicio');
    if (!formData.hora_fin) camposFaltantes.push('Hora de fin');
    if (!formData.descripcion_trabajo?.trim()) camposFaltantes.push('Descripción del trabajo');
    if (!formData.resultado_intervencion) camposFaltantes.push('Resultado de la intervención');

    if (!tieneOT && !formData.cliente_id && !formData.cliente_libre?.trim()) {
      camposFaltantes.push('Cliente (selecciona uno o escribe el nombre)');
    }

    // Si el cliente no tiene dirección en su ficha, exigir dirección de intervención manual
    if (!formData.direccion_intervencion?.trim() && !clienteTieneDireccion) {
      camposFaltantes.push('Dirección de la intervención (el cliente seleccionado no tiene dirección en su ficha)');
    }

    if (!firmaTecnico) camposFaltantes.push('Firma del técnico');
    if (!firmaCliente) camposFaltantes.push('Firma del cliente');

    if (camposFaltantes.length > 0) {
      toast({
        title: "Faltan datos para crear el parte",
        description: `Completa: ${camposFaltantes.join(', ')}.`,
        variant: "destructive",
      });
      return;
    }

    try {
      // Determinar cliente final (ya validado arriba)
      const finalClienteId = otSeleccionada?.cliente_id || formData.cliente_id || null;
      const finalClienteLibre = otSeleccionada ? '' : (formData.cliente_libre || '');

      const parteData = {
        ...formData,
        orden_trabajo_id: formData.orden_trabajo_id && formData.orden_trabajo_id !== 'none' ? formData.orden_trabajo_id : null,
        cliente_id: finalClienteId || null,
        cliente_libre: finalClienteLibre || null,
        tecnico_id: perfil.user_id,
        empresa_id: perfil.empresa_id,
        firma_tecnico: firmaTecnico,
        firma_cliente: firmaCliente,
        forma_pago: (formData.forma_pago || null) as 'efectivo' | 'transferencia' | 'tarjeta' | null,
        importe_sin_iva: formData.importe_sin_iva ? parseFloat(formData.importe_sin_iva) : null,
        resultado_intervencion: formData.resultado_intervencion || null,
        requiere_nueva_visita: formData.requiere_nueva_visita,
        motivo_no_resuelto: formData.motivo_no_resuelto || null,
        materiales_necesarios: formData.materiales_necesarios || null,
        direccion_intervencion: formData.direccion_intervencion?.trim() || null,
        codigo_postal_intervencion: formData.codigo_postal_intervencion?.trim() || null,
        poblacion_intervencion: formData.poblacion_intervencion?.trim() || null,
      };

      const createdParte = await createParte(parteData);

      // Reset form
      setFormData({
        orden_trabajo_id: '',
        cliente_libre: '',
        cliente_id: '',
        fecha_trabajo: new Date().toISOString().split('T')[0],
        hora_inicio: '',
        hora_fin: '',
        descripcion_trabajo: '',
        tipo_intervencion: 'otros',
        nota_interna: '',
        estado: 'borrador',
        tiene_desplazamiento: false,
        forma_pago: '',
        importe_sin_iva: '',
        iva_porcentaje: 21,
        resultado_intervencion: '',
        requiere_nueva_visita: false,
        motivo_no_resuelto: '',
        materiales_necesarios: '',
        direccion_intervencion: '',
        codigo_postal_intervencion: '',
        poblacion_intervencion: '',
      });
      tecnicoSignatureRef.current?.clear();
      clienteSignatureRef.current?.clear();
      setShowForm(false);
      
      // Ask if user wants to add documents and show numero_parte
      if (createdParte?.id) {
        setCreatedParteId(createdParte.id);
        setCreatedParteNumero(createdParte.numero_parte || null);
        setShowDocuments(true);
      }
    } catch (error: any) {
      console.error('Error creating parte:', error);
      toast({
        title: "No se pudo crear el parte de trabajo",
        description: error?.message || "Ha ocurrido un error inesperado. Revisa los datos e inténtalo de nuevo.",
        variant: "destructive",
      });
    }
  };

  const getEstadoBadge = (estado: string) => {
    const colorClasses: Record<string, string> = {
      borrador: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30',
      finalizado: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
      facturado: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
    };
    return <Badge variant="outline" className={colorClasses[estado] || ''}>{estado}</Badge>;
  };

  const formatDuration = (duration: unknown): string => {
    if (!duration || typeof duration !== 'string') return '-';
    
    // Parse PostgreSQL interval format (e.g., "02:30:00")
    const match = duration.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes] = match;
      return `${parseInt(hours)}h ${parseInt(minutes)}m`;
    }
    return duration;
  };

  const getTipoIntervencionText = (tipo: string): string => {
    switch (tipo) {
      case 'revision_sistema_seguridad':
        return 'Revisión seguridad';
      case 'averias':
        return 'Averías';
      case 'auditoria':
        return 'Auditoría';
      case 'entrega_materiales':
        return 'Entrega materiales';
      case 'otros':
        return 'Otros';
      default:
        return tipo;
    }
  };

  const getTipoIntervencionIcon = (tipo: string) => {
    switch (tipo) {
      case 'revision_sistema_seguridad':
        return <Shield className="w-4 h-4 text-primary" />;
      case 'averias':
        return <AlertTriangle className="w-4 h-4 text-destructive" />;
      case 'auditoria':
        return <ClipboardCheck className="w-4 h-4 text-raven-ice" />;
      case 'entrega_materiales':
        return <Package className="w-4 h-4 text-raven-violet" />;
      case 'otros':
        return <MoreHorizontal className="w-4 h-4 text-muted-foreground" />;
      default:
        return <MoreHorizontal className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const handleDownloadPdf = async (parte: any) => {
    let clienteNombre = '';
    let clienteData = null;
    
    if (parte.cliente_id) {
      const cliente = clientes.find(c => c.id === parte.cliente_id);
      if (cliente) {
        clienteNombre = cliente.nombre;
        clienteData = cliente;
      }
    } else if (parte.cliente_libre) {
      clienteNombre = parte.cliente_libre;
      clienteData = { nombre: parte.cliente_libre }; // Minimal client data for PDF
    }
    
    if (!clienteNombre) {
      toast({
        title: "Error",
        description: "No se pudo identificar el cliente",
        variant: "destructive",
      });
      return;
    }

    setLoadingPdf(parte.id);
    try {
      const { loadEmpresaYConfigParaPdf } = await import('@/lib/pdfBranding');
      const empresaId = (parte as any).empresa_id || perfil?.empresa_id;
      const { empresa, docConfig } = await loadEmpresaYConfigParaPdf(empresaId);

      // Resolver el nombre del técnico REAL del parte (no el usuario actual)
      let tecnicoNombre: string =
        (parte as any)?.tecnico?.nombre ||
        (parte as any)?.perfiles?.nombre ||
        '';
      if (!tecnicoNombre && (parte as any)?.tecnico_id) {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: tec } = await supabase
          .from('perfiles')
          .select('nombre')
          .eq('user_id', (parte as any).tecnico_id)
          .maybeSingle();
        tecnicoNombre = tec?.nombre || '';
      }
      if (!tecnicoNombre) tecnicoNombre = 'Técnico';

      await downloadParteTrabajoePdf(parte, clienteNombre, tecnicoNombre, clienteData, empresa, docConfig);
      toast({
        title: "PDF descargado",
        description: "El parte de trabajo se ha descargado correctamente",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Error",
        description: "Error al descargar el PDF",
        variant: "destructive",
      });
    } finally {
      setLoadingPdf(null);
    }
  };

  const handleStatusChange = async (parteId: string, newStatus: 'borrador' | 'finalizado' | 'facturado') => {
    try {
      await updateParte(parteId, { estado: newStatus });
      toast({
        title: "Estado actualizado",
        description: "El estado del parte de trabajo se ha actualizado correctamente",
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Error al actualizar el estado",
        variant: "destructive",
      });
    }
  };

  // Get row color class based on estado for admin users
  const getRowColorClass = (estado: string) => {
    if (perfil?.rol !== 'admin') return '';
    
    switch (estado) {
      case 'borrador':
        return 'status-row-draft';
      case 'finalizado':
        return 'status-row-finished';
      case 'facturado':
        return 'status-row-invoiced';
      default:
        return '';
    }
  };

  // Apply filters and role-based filtering
  const allFilteredPartes = getFilteredPartes(filters);
  const isOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';
  const filteredPartes = isOffice
    ? allFilteredPartes 
    : allFilteredPartes.filter(parte => parte.tecnico_id === perfil?.user_id);
  
  const estadoCounts = getEstadoCounts();
  const tecnicos = [];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  const noFilters = Object.values(filters).every(v => !v || v === '' || v === 'all');
  const showEmptyState = !loading && filteredPartes.length === 0 && noFilters;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Partes de Trabajo</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona los partes de trabajo con firmas digitales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-background p-0.5">
            <Button
              type="button"
              variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode('cards')}
              aria-label="Vista de tarjetas"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode('list')}
              aria-label="Vista de lista"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Parte
          </Button>
        </div>
      </div>

      {/* Filters */}
      <PartesTrabajoFilters
        filters={filters}
        onFiltersChange={setFilters}
        clientes={clientes}
        tecnicos={tecnicos}
        showTecnicoFilter={isOffice}
        resultCount={filteredPartes.length}
        estadoCounts={estadoCounts}
      />

      {showEmptyState && !showForm && (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-muted-foreground">No hay partes de trabajo</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Crea tu primer parte de trabajo para comenzar
            </p>
          </div>
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Nuevo Parte de Trabajo</span>
              {nextNumero && (
                <Badge variant="secondary" className="font-mono text-sm">
                  {nextNumero}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Orden de Trabajo */}
                <div className="space-y-2">
                  <Label htmlFor="orden_trabajo">Orden de Trabajo (Opcional)</Label>
                  <Select
                    value={formData.orden_trabajo_id}
                    onValueChange={(value) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        orden_trabajo_id: value,
                        cliente_id: value ? '' : prev.cliente_id // Clear client selection if OT is selected
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar orden de trabajo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Trabajo sin orden previa</SelectItem>
                      {ordenesAsignadas.map((orden) => (
                        <SelectItem key={orden.id} value={orden.id}>
                          {orden.numero_ot} - {orden.titulo} - {orden.cliente?.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cliente libre - solo si no hay OT seleccionada */}
                {(!formData.orden_trabajo_id || formData.orden_trabajo_id === 'none') && (
                  <div className="space-y-2">
                    <Label htmlFor="cliente_libre">Cliente</Label>
                    {perfil?.rol === 'admin' || perfil?.rol === 'tecnico' || perfil?.rol === 'gestor' ? (
                      <>
                        <Select
                          value={formData.cliente_id}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, cliente_id: value, cliente_libre: '' }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar cliente" />
                          </SelectTrigger>
                          <SelectContent>
                            {clientesParaParte.map((cliente) => (
                              <SelectItem key={cliente.id} value={cliente.id}>
                                {cliente.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!formData.cliente_id && (
                          <Input
                            id="cliente_libre"
                            value={formData.cliente_libre}
                            onChange={(e) => setFormData(prev => ({ ...prev, cliente_libre: e.target.value }))}
                            placeholder="O escribe el nombre del cliente..."
                          />
                        )}
                      </>
                    ) : (
                      <Input
                        id="cliente_libre"
                        value={formData.cliente_libre}
                        onChange={(e) => setFormData(prev => ({ ...prev, cliente_libre: e.target.value }))}
                        placeholder="Nombre del cliente..."
                        required
                      />
                    )}
                  </div>
                )}

                {/* Show OT info if selected */}
                {formData.orden_trabajo_id && formData.orden_trabajo_id !== 'none' && (
                  <div className="md:col-span-2">
                    {(() => {
                      const selectedOT = ordenesAsignadas.find(ot => ot.id === formData.orden_trabajo_id);
                      if (!selectedOT) return null;
                      
                      return (
                        <Card className="bg-primary/10 border-primary/30">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <ClipboardList className="w-4 h-4" />
                              Información de la Orden de Trabajo
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="font-medium">Cliente:</Label>
                                <p className="text-muted-foreground">{selectedOT.cliente?.nombre}</p>
                              </div>
                              <div>
                                <Label className="font-medium">Prioridad:</Label>
                                <p className="text-muted-foreground capitalize">{selectedOT.prioridad}</p>
                              </div>
                              {selectedOT.descripcion && (
                                <div className="col-span-2">
                                  <Label className="font-medium">Descripción:</Label>
                                  <p className="text-muted-foreground">{selectedOT.descripcion}</p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="fecha">Fecha</Label>
                  <Input
                    id="fecha"
                    type="date"
                    value={formData.fecha_trabajo}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_trabajo: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hora_inicio">Hora Inicio</Label>
                  <TimeSelect
                    value={formData.hora_inicio}
                    onChange={(value) => setFormData(prev => ({ ...prev, hora_inicio: value }))}
                    placeholder="Seleccionar hora"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hora_fin">Hora Fin</Label>
                  <TimeSelect
                    value={formData.hora_fin}
                    onChange={(value) => setFormData(prev => ({ ...prev, hora_fin: value }))}
                    placeholder="Seleccionar hora"
                  />
                </div>
              </div>

              {/* Dirección de la intervención */}
              <div className="space-y-3 p-4 rounded-md border bg-muted/30">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="flex items-center gap-2 text-base">
                    <MapPin className="w-4 h-4" />
                    Dirección de la intervención
                  </Label>
                  {(() => {
                    const otCliente = formData.orden_trabajo_id && formData.orden_trabajo_id !== 'none'
                      ? ordenesAsignadas.find(ot => ot.id === formData.orden_trabajo_id)?.cliente
                      : null;
                    const cliente: any = otCliente || findClienteForForm(formData.cliente_id);
                    if (!cliente?.direccion && !cliente?.poblacion) return null;
                    return (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          direccion_intervencion: cliente.direccion || '',
                          codigo_postal_intervencion: cliente.codigo_postal || '',
                          poblacion_intervencion: cliente.poblacion || '',
                        }))}
                      >
                        Usar dirección del cliente
                      </Button>
                    );
                  })()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Déjalo en blanco para usar la dirección fiscal del cliente. Útil para clientes nuevos o ubicaciones distintas a su sede.
                </p>
                <Input
                  value={formData.direccion_intervencion}
                  onChange={(e) => setFormData(prev => ({ ...prev, direccion_intervencion: e.target.value }))}
                  placeholder="Calle, número, piso..."
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={formData.codigo_postal_intervencion}
                    onChange={(e) => setFormData(prev => ({ ...prev, codigo_postal_intervencion: e.target.value }))}
                    placeholder="C.P."
                    className="col-span-1"
                  />
                  <Input
                    value={formData.poblacion_intervencion}
                    onChange={(e) => setFormData(prev => ({ ...prev, poblacion_intervencion: e.target.value }))}
                    placeholder="Población"
                    className="col-span-2"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción del Trabajo</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion_trabajo}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion_trabajo: e.target.value }))}
                  placeholder="Describe el trabajo realizado..."
                  rows={4}
                  required
                />
              </div>

              {/* Tipo de Intervención */}
              <div className="space-y-2">
                <Label htmlFor="tipo_intervencion" className="flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  Tipo de Intervención
                </Label>
                <Select
                  value={formData.tipo_intervencion}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_intervencion: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo de intervención" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revision_sistema_seguridad">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Revisión sistema de seguridad
                      </div>
                    </SelectItem>
                    <SelectItem value="averias">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Averías
                      </div>
                    </SelectItem>
                    <SelectItem value="auditoria">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" />
                        Auditoría
                      </div>
                    </SelectItem>
                    <SelectItem value="entrega_materiales">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Entrega materiales
                      </div>
                    </SelectItem>
                    <SelectItem value="otros">
                      <div className="flex items-center gap-2">
                        <MoreHorizontal className="w-4 h-4" />
                        Otros
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Nota Interna - Solo para administradores */}
              {isOffice && (
                <div className="space-y-2">
                  <Label htmlFor="nota_interna" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Nota Interna (Solo oficina)
                  </Label>
                  <Textarea
                    id="nota_interna"
                    value={formData.nota_interna}
                    onChange={(e) => setFormData(prev => ({ ...prev, nota_interna: e.target.value }))}
                    placeholder="Información confidencial para uso interno..."
                    rows={3}
                    className="border-orange-200 focus:border-orange-500"
                  />
                </div>
              )}

              {/* Datos del Cliente - solo si admin y cliente seleccionado */}
              {formData.cliente_id && isOffice && !formData.orden_trabajo_id && (
                <Card className="bg-muted/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Datos del Cliente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const selectedCliente: any = findClienteForForm(formData.cliente_id);
                      if (!selectedCliente) return <p className="text-muted-foreground">Cliente no encontrado</p>;
                      
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <Label className="font-medium">Nombre:</Label>
                            <p className="text-muted-foreground">{selectedCliente.nombre}</p>
                          </div>
                          {selectedCliente.nif_cif && (
                            <div>
                              <Label className="font-medium">NIF/CIF:</Label>
                              <p className="text-muted-foreground">{selectedCliente.nif_cif}</p>
                            </div>
                          )}
                          {selectedCliente.direccion && (
                            <div>
                              <Label className="font-medium">Dirección:</Label>
                              <p className="text-muted-foreground">{selectedCliente.direccion}</p>
                            </div>
                          )}
                          {selectedCliente.poblacion && (
                            <div>
                              <Label className="font-medium">Población:</Label>
                              <p className="text-muted-foreground">
                                {selectedCliente.poblacion}
                                {selectedCliente.codigo_postal && ` (${selectedCliente.codigo_postal})`}
                              </p>
                            </div>
                          )}
                          {selectedCliente.telefono && (
                            <div>
                              <Label className="font-medium">Teléfono:</Label>
                              <p className="text-muted-foreground">{selectedCliente.telefono}</p>
                            </div>
                          )}
                          {selectedCliente.email && (
                            <div>
                              <Label className="font-medium">Email:</Label>
                              <p className="text-muted-foreground">{selectedCliente.email}</p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Cliente libre info */}
              {formData.cliente_libre && !formData.orden_trabajo_id && perfil?.rol !== 'admin' && (
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Cliente: {formData.cliente_libre}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Trabajo realizado para cliente no registrado en el sistema
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Opciones Adicionales */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Opciones Adicionales</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Checkbox Desplazamiento */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tiene_desplazamiento"
                      checked={formData.tiene_desplazamiento}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, tiene_desplazamiento: checked as boolean }))
                      }
                    />
                    <Label htmlFor="tiene_desplazamiento" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Se ha realizado desplazamiento
                    </Label>
                  </div>

                  {/* Forma de Pago */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forma_pago" className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Forma de Pago
                      </Label>
                      <Select
                        value={formData.forma_pago}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, forma_pago: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar forma de pago" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="efectivo">Efectivo</SelectItem>
                          <SelectItem value="transferencia">Transferencia</SelectItem>
                          <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Campos de Importe - Solo si es efectivo */}
                    {formData.forma_pago === 'efectivo' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="space-y-2">
                          <Label htmlFor="importe_sin_iva" className="flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            Importe sin IVA
                          </Label>
                          <Input
                            id="importe_sin_iva"
                            type="number"
                            step="0.01"
                            value={formData.importe_sin_iva}
                            onChange={(e) => setFormData(prev => ({ ...prev, importe_sin_iva: e.target.value }))}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="iva_porcentaje">% IVA</Label>
                          <Input
                            id="iva_porcentaje"
                            type="number"
                            value={formData.iva_porcentaje}
                            onChange={(e) => setFormData(prev => ({ ...prev, iva_porcentaje: parseInt(e.target.value) || 21 }))}
                            readOnly
                            className="bg-muted"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            Importe con IVA
                          </Label>
                          <div className="h-10 flex items-center px-3 py-2 border rounded-md bg-muted text-sm font-medium">
                            {formData.importe_sin_iva ? 
                              (parseFloat(formData.importe_sin_iva) * (1 + formData.iva_porcentaje / 100)).toFixed(2) + ' €' : 
                              '0.00 €'
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Resultado de la Intervención */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5" />
                    Resultado de la Intervención
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="resultado_intervencion">Estado del Trabajo *</Label>
                    <Select
                      value={formData.resultado_intervencion}
                      onValueChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        resultado_intervencion: value as 'resuelto' | 'no_resuelto' | 'no_realizado'
                      }))}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar resultado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resuelto">✅ Trabajo completado - Problema resuelto</SelectItem>
                        <SelectItem value="no_resuelto">⚠️ Trabajo realizado - Problema no resuelto</SelectItem>
                        <SelectItem value="no_realizado">❌ Trabajo no realizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Campos condicionales según el resultado */}
                  {(formData.resultado_intervencion === 'no_resuelto' || formData.resultado_intervencion === 'no_realizado') && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="motivo_no_resuelto">
                          {formData.resultado_intervencion === 'no_resuelto' ? 'Motivo por el que no se resolvió' : 'Motivo por el que no se realizó'}
                        </Label>
                        <Textarea
                          id="motivo_no_resuelto"
                          value={formData.motivo_no_resuelto}
                          onChange={(e) => setFormData(prev => ({ ...prev, motivo_no_resuelto: e.target.value }))}
                          placeholder={formData.resultado_intervencion === 'no_resuelto' 
                            ? "Explica por qué no se pudo resolver el problema..." 
                            : "Explica por qué no se pudo realizar el trabajo..."
                          }
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="materiales_necesarios">Materiales/Recursos Necesarios</Label>
                        <Textarea
                          id="materiales_necesarios"
                          value={formData.materiales_necesarios}
                          onChange={(e) => setFormData(prev => ({ ...prev, materiales_necesarios: e.target.value }))}
                          placeholder="Lista los materiales o recursos necesarios para completar el trabajo..."
                          rows={2}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="requiere_nueva_visita"
                          checked={formData.requiere_nueva_visita}
                          onCheckedChange={(checked) => 
                            setFormData(prev => ({ ...prev, requiere_nueva_visita: checked as boolean }))
                          }
                        />
                        <Label htmlFor="requiere_nueva_visita" className="text-sm font-medium">
                          Se requiere una nueva visita
                        </Label>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SignatureCanvas
                  ref={tecnicoSignatureRef}
                  title="Firma del Técnico"
                />
                <SignatureCanvas
                  ref={clienteSignatureRef}
                  title="Firma del Cliente"
                />
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="flex-1">
                  Crear Parte de Trabajo
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Lista de Partes de Trabajo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPartes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay partes de trabajo registrados</p>
              <p className="text-sm">Crea el primer parte de trabajo</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[110px]">Nº Parte</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="hidden md:table-cell">Tipo</TableHead>
                    <TableHead className="hidden sm:table-cell">Fecha</TableHead>
                    <TableHead className="hidden lg:table-cell">Horario</TableHead>
                    <TableHead className="hidden lg:table-cell">Técnico</TableHead>
                    <TableHead className="hidden md:table-cell">OT</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartes.map((parte) => {
                    const cliente = clientes.find(c => c.id === parte.cliente_id);
                    const clienteNombre = cliente?.nombre || parte.cliente_libre || 'Cliente no encontrado';
                    return (
                      <TableRow key={parte.id} className={getRowColorClass(parte.estado)}>
                        <TableCell className="font-mono text-xs">
                          {parte.numero_parte || 'Pendiente'}
                        </TableCell>
                        <TableCell className="font-medium max-w-[220px] truncate">
                          {clienteNombre}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1.5 text-sm">
                            {getTipoIntervencionIcon(parte.tipo_intervencion)}
                            <span className="truncate">{getTipoIntervencionText(parte.tipo_intervencion)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm whitespace-nowrap">
                          {format(new Date(parte.fecha_trabajo), 'dd/MM/yyyy', { locale: es })}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm whitespace-nowrap text-muted-foreground">
                          {parte.hora_inicio ? (
                            <>
                              {parte.hora_inicio}{parte.hora_fin ? `–${parte.hora_fin}` : ''}
                              {parte.duracion_total ? ` (${formatDuration(parte.duracion_total)})` : ''}
                            </>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[140px] truncate">
                          {(parte as any)?.tecnico?.nombre || (parte as any)?.perfiles?.nombre || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {parte.orden_trabajo_id ? (
                            <OrdenTrabajoLink
                              ordenId={parte.orden_trabajo_id}
                              numeroOt={parte.orden_trabajo?.numero_ot}
                              titulo={parte.orden_trabajo?.titulo}
                            />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isOffice ? (
                            <Select
                              value={parte.estado}
                              onValueChange={(value) => handleStatusChange(parte.id, value as 'borrador' | 'finalizado' | 'facturado')}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="borrador">Borrador</SelectItem>
                                <SelectItem value="finalizado">Finalizado</SelectItem>
                                <SelectItem value="facturado">Facturado</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            getEstadoBadge(parte.estado)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setCreatedParteId(parte.id);
                                setShowDocuments(true);
                              }}
                              title="Documentos"
                            >
                              <Paperclip className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadPdf(parte)}
                              disabled={loadingPdf === parte.id}
                              title="Descargar PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPartes.map((parte) => {
                const cliente = clientes.find(c => c.id === parte.cliente_id);
                const clienteNombre = cliente?.nombre || parte.cliente_libre || 'Cliente no encontrado';
                const dirOverride = (parte as any).direccion_intervencion;
                const direccionMostrada = dirOverride || cliente?.direccion;
                const accentClass =
                  parte.estado === 'borrador' ? 'border-l-orange-500' :
                  parte.estado === 'finalizado' ? 'border-l-blue-500' :
                  parte.estado === 'facturado' ? 'border-l-emerald-500' : 'border-l-muted';
                return (
                  <Card
                    key={parte.id}
                    className={`border-l-4 ${accentClass} ${getRowColorClass(parte.estado)} transition-colors hover:bg-muted/40`}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="mt-0.5 shrink-0">
                            {getTipoIntervencionIcon(parte.tipo_intervencion)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm text-muted-foreground">
                                {parte.numero_parte || 'Pendiente'}
                              </span>
                              <span className="font-semibold truncate">{clienteNombre}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                              <span>{getTipoIntervencionText(parte.tipo_intervencion)}</span>
                              {parte.orden_trabajo_id && (
                                <>
                                  <span>·</span>
                                  <OrdenTrabajoLink
                                    ordenId={parte.orden_trabajo_id}
                                    numeroOt={parte.orden_trabajo?.numero_ot}
                                    titulo={parte.orden_trabajo?.titulo}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0">
                          {isOffice ? (
                            <Select
                              value={parte.estado}
                              onValueChange={(value) => handleStatusChange(parte.id, value as 'borrador' | 'finalizado' | 'facturado')}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="borrador">Borrador</SelectItem>
                                <SelectItem value="finalizado">Finalizado</SelectItem>
                                <SelectItem value="facturado">Facturado</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            getEstadoBadge(parte.estado)
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-x-4 gap-y-1 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {format(new Date(parte.fecha_trabajo), 'dd/MM/yyyy', { locale: es })}
                          {parte.hora_inicio && (
                            <span className="ml-1">
                              · {parte.hora_inicio}{parte.hora_fin ? `–${parte.hora_fin}` : ''}
                              {parte.duracion_total ? ` (${formatDuration(parte.duracion_total)})` : ''}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {(parte as any)?.tecnico?.nombre || (parte as any)?.perfiles?.nombre || 'Técnico'}
                        </span>
                        {direccionMostrada && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">{direccionMostrada}</span>
                          </span>
                        )}
                      </div>

                      {parte.descripcion_trabajo && (
                        <p className="text-sm line-clamp-2 text-foreground/80">
                          {parte.descripcion_trabajo}
                        </p>
                      )}

                      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          {parte.tiene_desplazamiento && (
                            <Badge variant="outline" className="gap-1 font-normal">
                              <MapPin className="w-3 h-3" />
                              Desplazamiento
                            </Badge>
                          )}
                          {parte.forma_pago && (
                            <Badge variant="outline" className="gap-1 font-normal capitalize">
                              <CreditCard className="w-3 h-3" />
                              {parte.forma_pago}
                              {parte.forma_pago === 'efectivo' && parte.importe_con_iva && (
                                <span className="font-medium ml-1">{parte.importe_con_iva}€</span>
                              )}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCreatedParteId(parte.id);
                              setShowDocuments(true);
                            }}
                            className="gap-1"
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Docs</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPdf(parte)}
                            disabled={loadingPdf === parte.id}
                            className="gap-1"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{loadingPdf === parte.id ? 'Descargando…' : 'PDF'}</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents Dialog */}
      {showDocuments && createdParteId && (
        <Card className="fixed inset-0 z-50 bg-background/95 backdrop-blur">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Parte Creado: {createdParteNumero || 'Sin número'}
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setShowDocuments(false);
                setCreatedParteId(null);
                setCreatedParteNumero(null);
              }}
            >
              ✕
            </Button>
          </CardHeader>
          <CardContent className="max-h-[80vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground mb-4">
              ¿Desea añadir fotos o documentos al parte de trabajo?
            </p>
            <ParteTrabajoDocumentos 
              parteTrabajoId={createdParteId} 
              readonly={false}
            />
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={() => {
                  setShowDocuments(false);
                  setCreatedParteId(null);
                  setCreatedParteNumero(null);
                }}
              >
                Finalizar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}